"use client"

import { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import withUser from '@hoc/withUser'
import salonLogo from '@utils/links/salonLogo'
import { notify, notifyAfterNavigation } from 'lib/notice/notice'
import { PRESETS } from 'lib/chat/presets'
import { LOGIN_PAGE, MESSAGES_PAGE } from '@constants/routes'
import WriteDialog from '@components/messages/WriteDialog'
import { chatApi } from '@components/messages/api'

const HOST = process.env.NEXT_PUBLIC_HOST
const DRAFT_KEY = 'spl:napisz-firma'
const DRAFT_TTL = 30 * 60 * 1000
const HASH = '#napisz'
let autoOpened = false

const visible = (el) => Boolean(el?.getClientRects().length)
const forSalon = ([, token, id]) => chatApi(token, `/api/messages/for-salon?salon=${encodeURIComponent(id)}`).then(r => r.body.conversation || null)

function takeDraft () {
    try {
        const raw = sessionStorage.getItem(DRAFT_KEY)
        sessionStorage.removeItem(DRAFT_KEY)
        const d = raw && JSON.parse(raw)
        return d && d.path === location.pathname && Date.now() - d.at < DRAFT_TTL ? String(d.text || '') : ''
    } catch (err) {
        return ''
    }
}

// „Napisz do firmy” (nagłówek, pasek na telefonie, stan bez ogłoszeń): własna wizytówka — komunikat; istniejąca rozmowa —
// przejście do niej; inaczej okno pierwszej wiadomości. Gość — „Zaloguj się i wyślij”, tekst zachowany, powrót na
// wizytówkę (#napisz) z otwartym oknem.
function SalonWrite ({ className, firm, isLoggedIn: storedLogin, label = 'Napisz do firmy', short, user, visitor }) {
    // na subdomenie dane logowania z przeglądarki są niedostępne — token zalogowanego z serwera
    const isLoggedIn = Boolean(visitor?.token) || storedLogin
    const [open, setOpen] = useState(false)
    const [draft, setDraft] = useState('')
    const btnRef = useRef(null)
    const token = visitor?.token || (storedLogin ? user?.token : null)
    const { data: existing } = useSWR(token ? ['msg-salon', token, firm._id] : null, forSalon, { revalidateOnFocus: false })
    const target = { _id: firm._id, name: firm.name || firm.legal, city: firm.city, logo: firm.hasLogo ? salonLogo(firm._id) : '' }

    useEffect(() => {
        if (autoOpened || !isLoggedIn || location.hash !== HASH || !visible(btnRef.current)) return
        if (token && existing === undefined) return
        autoOpened = true
        history.replaceState(history.state, '', location.pathname + location.search)
        const text = takeDraft()
        if (existing?.id || existing?.own) return
        setDraft(text)
        setOpen(true)
        if (text) notify('Zalogowano. Sprawdź wiadomość i kliknij „Wyślij”.')
    }, [isLoggedIn, token, existing])

    const onClick = (e) => {
        if (existing?.own) return notify('To jest wizytówka Twojej firmy.', { opener: e.currentTarget })
        if (existing?.id) { window.location.href = `${HOST}${MESSAGES_PAGE}/${existing.id}`; return }
        setDraft('')
        setOpen(true)
    }

    // logowanie na domenie serwisu, powrót na ten adres wizytówki (także subdomenę) z otwartym oknem
    const toLogin = (text) => {
        try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ path: location.pathname, text, at: Date.now() })) } catch (err) { /* bez zapisu tekstu */ }
        const back = location.href.split('#')[0] + HASH
        window.location.href = `${HOST}${LOGIN_PAGE}?powrot=${encodeURIComponent(back)}`
    }

    // rozmowa na domenie serwisu (komunikat po przejściu tylko w obrębie tej samej domeny)
    const onSent = (id) => {
        if (location.origin === HOST) notifyAfterNavigation('Wiadomość wysłana. Odpowiedź pojawi się w tej rozmowie.')
        window.location.href = `${HOST}${MESSAGES_PAGE}/${id}`
    }

    const text = existing?.id ? (short ? 'Rozmowa' : 'Przejdź do rozmowy') : label
    return <>
        <button className={className} onClick={onClick} ref={btnRef} type="button"><SvgIcon name="chat" />{text}</button>
        { open && <WriteDialog draft={draft} firm={target} guest={!isLoggedIn} onClose={() => setOpen(false)} onLogin={toLogin} onSent={onSent} opener={btnRef.current} presets={PRESETS[firm.presetKind] || PRESETS.salon} token={token} /> }
    </>
}

export default withUser(SalonWrite)
